import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
  Tooltip,
  Badge,
  alpha,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
  LocationOn as LocationIcon,
  SwapHoriz as TransferIcon,
  Assessment as ReportIcon,
  History as AuditIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  QrCode as QRIcon,
  CloudUpload as UploadIcon,
  ChevronLeft as ChevronLeftIcon,
  Notifications as NotificationsIcon,
  Print as PrintIcon,
  FactCheck as AuditCheckIcon,
  Dataset as DatasetIcon,
  Assignment as PlanIcon,
} from '@mui/icons-material';

import { useAuthStore } from '../stores/authStore';
import { authApi } from '../services/api';
import { toast } from 'react-toastify';

const DRAWER_WIDTH = 280;
const DRAWER_WIDTH_COLLAPSED = 72;

interface NavItem {
  title: string;
  path: string;
  icon: React.ReactNode;
  permission?: string;
  roles?: string[];
}

const navItems: NavItem[] = [
  { title: 'Dashboard', path: '/dashboard', icon: <DashboardIcon />, permission: 'dashboard:read' },
  { title: 'Assets', path: '/assets', icon: <InventoryIcon />, permission: 'assets:read' },
  { title: 'QR Codes', path: '/qr', icon: <QRIcon />, permission: 'qrcodes:read' },
  { title: 'Asset Audit', path: '/asset-audit', icon: <AuditCheckIcon />, permission: 'assets:read' },
  { title: 'Audit Plan', path: '/audit-plan', icon: <PlanIcon />, permission: 'auditplan:read' },
  { title: 'Label Printing', path: '/label-printing', icon: <PrintIcon />, permission: 'labelprinting:read' },
  { title: 'Locations', path: '/locations', icon: <LocationIcon />, permission: 'locations:read' },
  { title: 'Transfers', path: '/transfers', icon: <TransferIcon />, permission: 'transfers:read' },
  { title: 'Users', path: '/users', icon: <PeopleIcon />, permission: 'users:read' },
  { title: 'Roles', path: '/roles', icon: <SecurityIcon />, roles: ['superadmin'] },
  { title: 'Master Data', path: '/master-data', icon: <DatasetIcon />, permission: 'settings:read' },
  { title: 'Bulk Upload', path: '/bulk-upload', icon: <UploadIcon />, permission: 'bulkupload:create' },
  { title: 'Reports', path: '/reports', icon: <ReportIcon />, permission: 'reports:read' },
  { title: 'Audit Logs', path: '/audit-logs', icon: <AuditIcon />, permission: 'audit:read' },
  { title: 'Settings', path: '/settings', icon: <SettingsIcon />, permission: 'settings:read' },
];

const MainLayout: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { user, logout, hasPermission, hasRole } = useAuthStore();
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setCollapsed(!collapsed);
    }
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      // Ignore logout errors
    } finally {
      logout();
      toast.success('Logged out successfully');
      navigate('/login');
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const isActiveRoute = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const filteredNavItems = navItems.filter((item) => {
    if (item.roles && !hasRole(...item.roles)) return false;
    if (item.permission && !hasPermission(item.permission)) return false;
    return true;
  });

  const drawerWidth = collapsed && !isMobile ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          p: collapsed ? 1.5 : 2,
          minHeight: 72,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
        }}
      >
        {collapsed ? (
          // Collapsed state - just show logo
          <Tooltip title="Lucas TVS Asset Management" placement="right" arrow>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                overflow: 'hidden',
                cursor: 'pointer',
              }}
              onClick={handleDrawerToggle}
            >
              <img 
                src="/lucas-tvs-logo.png"
                alt="Lucas TVS"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  backgroundColor: '#fff',
                }}
              />
            </Box>
          </Tooltip>
        ) : (
          // Expanded state - show logo with text
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                flexShrink: 0,
                backgroundColor: '#fff',
              }}
            >
              <img
                src="/lucas-tvs-logo.png"
                alt="Lucas TVS"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>
            <Box>
              <Typography 
                sx={{ 
                  fontWeight: 800, 
                  fontSize: '1rem',
                  color: '#fff',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                }}
              >
                Lucas TVS
              </Typography>
              <Typography variant="caption" sx={{ color: alpha('#fff', 0.85), fontWeight: 500, display: 'block', letterSpacing: '0.02em', fontSize: '0.7rem' }}>
                Asset Management
              </Typography>
            </Box>
          </Box>
        )}
        {!isMobile && !collapsed && (
          <IconButton onClick={handleDrawerToggle} sx={{ color: '#fff' }}>
            <ChevronLeftIcon sx={{ transition: 'transform 0.2s' }} />
          </IconButton>
        )}
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 2 }}>
        <List>
          {filteredNavItems.map((item) => {
            const isActive = isActiveRoute(item.path);
            return (
              <ListItem key={item.path} disablePadding sx={{ px: 1 }}>
                <Tooltip title={collapsed ? item.title : ''} placement="right" arrow>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      px: collapsed ? 2 : 2.5,
                      py: 1.25,
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      backgroundColor: isActive ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                      color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      },
                      '& .MuiListItemIcon-root': {
                        color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: collapsed ? 0 : 40,
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {!collapsed && (
                      <ListItemText
                        primary={item.title}
                        primaryTypographyProps={{
                          fontWeight: isActive ? 600 : 400,
                          fontSize: '0.9375rem',
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* User Info */}
      {!collapsed && user && (
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: theme.palette.primary.main,
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {user.firstName[0]}{user.lastName[0]}
            </Avatar>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {user.firstName} {user.lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: '#fff',
          color: theme.palette.text.primary,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          transition: 'width 0.2s, margin-left 0.2s',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" fontWeight={600} sx={{ display: { xs: 'none', sm: 'block' } }}>
              {filteredNavItems.find((item) => isActiveRoute(item.path))?.title || 'Dashboard'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Notifications">
              <IconButton>
                <Badge badgeContent={0} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            <IconButton onClick={handleProfileMenuOpen}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: theme.palette.primary.main,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {user?.firstName[0]}{user?.lastName[0]}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1.5,
            minWidth: 200,
            borderRadius: 2,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {user?.firstName} {user?.lastName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.email}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => navigate('/profile')}>
          <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => navigate('/settings')}>
          <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 }, transition: 'width 0.2s' }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              transition: 'width 0.2s',
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          minHeight: '100vh',
          backgroundColor: '#f8f9fc',
          transition: 'width 0.2s, margin-left 0.2s',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default MainLayout;

